// Attached payments — Grant D. Attach/detach without a Kind A card.
// Reuses the #825 candidates GET + attach/detach POST. Does not send queue:.
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { AdminApiError, type AdminClient } from "../api.js";
import { clear, h, safeText } from "../dom.js";
import type {
  CompanySlug,
  PriorPaymentCandidate,
  StaffRole,
  VendorIdentitySnapshotGet,
  VendorIdentitySnapshotItem,
} from "../types.js";
import { button, disclosure, pageHeader, svgIcon } from "../ui/components.js";
import { accountLabel, dateText, moneyText } from "../ui/display-words.js";
import * as face from "../ui/face.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { readerRole } from "../ui/facts.js";
import { navLabel } from "../ui/sentences.js";
import { runWrite, type WriteOutcome } from "../ui/write.js";
import {
  ATTACH_MOVE_CONFIRM_COPY,
  ATTACH_NON_1099_CONFIRM_COPY,
  ATTACH_NOT_ATTACHABLE_COPY,
  ATTACH_NOT_ATTACHABLE_WORKER_MESSAGE,
  ATTACH_TWINS_COPY,
  ATTACH_TWINS_WORKER_MESSAGE,
  attachPriorPaymentsEmptyCopy,
} from "./queue.js";

const DEFAULT_YEAR = "2026";

export interface AttachedPaymentsPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  isCurrent?: () => boolean;
  /** Signed-in role, for the shared failure sentences. */
  role?: StaffRole;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1). Without it an account between
   * companies reads "another company", never its internal code.
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
}

/** One money formatter for the whole console (rd Y0). */
function formatAmount(amount: string): string {
  return moneyText(amount, { fallback: "No amount" });
}

/** One date formatter for the whole console (rd Y0). */
function formatDisplayDate(value: string): string {
  const shown = dateText(value);
  return shown === "" ? "Unknown date" : shown;
}

function classificationLabel(item: VendorIdentitySnapshotItem): string {
  if (item.classification === "1099_contractor") return "1099 contractor";
  if (item.classification === "non_1099_vendor") return "non-1099 vendor";
  return "unclassified";
}

function attachable(item: VendorIdentitySnapshotItem): boolean {
  return item.active && item.classification !== null && item.vendorId.startsWith("vid-");
}

function vendorFaceLabel(
  vendorId: string,
  identities: readonly VendorIdentitySnapshotItem[],
): string {
  const match = identities.find((item) => item.vendorId === vendorId);
  const text = amVisibleDisplayText(match?.matchText ?? "").trim();
  return text.length > 0 ? text : "Another vendor";
}

export function candidatesPeriodForYear(year: string): string {
  return `${year}-12`;
}

export async function renderAttachedPaymentsPage(
  opts: AttachedPaymentsPageOptions,
): Promise<void> {
  const { client, company, root } = opts;
  const role = readerRole(opts.role);
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);

  let year = DEFAULT_YEAR;
  /** The year the rows and the window caption were actually loaded for. */
  let loadedYear = DEFAULT_YEAR;
  /** One token per candidate read: a late answer never paints over a newer one. */
  let loadToken = 0;
  let snapshot: VendorIdentitySnapshotGet | null = null;
  let candidates: PriorPaymentCandidate[] = [];
  let selectedVendorId = "";

  const header = pageHeader(
    navLabel("attached-payments"),
    `Posted payments for this company. You can detach one here even when nothing in ${navLabel("queue")} is open. ` +
      `Attach only to a vendor already on file. New vendors are still declared in ${navLabel("queue")}.`,
  );
  header.querySelector("h1")?.setAttribute("data-role", "attached-payments-title");
  header.querySelector("p")?.setAttribute("data-role", "attached-payments-lede");
  root.append(header);

  const live = h(
    "div",
    { class: "banner", role: "status", "data-role": "attached-payments-live" },
  );
  root.append(live);

  const toolbar = h("div", { class: "toolbar", "data-role": "attached-payments-toolbar" });
  const yearLabel = h("span", { "data-role": "attached-payments-year" }, [year]);
  const prev = h(
    "button",
    {
      type: "button",
      class: "period-chevron",
      "data-action": "year-prev",
      "aria-label": "Previous year",
    },
    ["‹"],
  );
  const next = h(
    "button",
    {
      type: "button",
      class: "period-chevron",
      "data-action": "year-next",
      "aria-label": "Next year",
    },
    ["›"],
  );
  toolbar.append(prev, yearLabel, next);
  root.append(toolbar);
  root.append(
    h("p", { class: "muted", "data-role": "attached-payments-window" }, [
      `Window is 1 January through 31 December ${loadedYear}.`,
    ]),
  );

  const vendorHost = h("div", { "data-role": "attached-payments-vendor-host" });
  root.append(vendorHost);

  const list = h("div", {
    class: "attached-payments-list",
    "data-role": "attached-payments-list",
  });
  root.append(list);

  const attachNon1099Check = h("input", {
    type: "checkbox",
    "data-role": "prior-payment-non-1099-check",
  });
  const attachNon1099Confirm = h(
    "label",
    {
      class: "make-rule-opt-in paperwork-check",
      "data-role": "prior-payment-non-1099-confirm",
      hidden: "true",
    },
    [attachNon1099Check, ` ${ATTACH_NON_1099_CONFIRM_COPY}`],
  );
  const attachMoveCheck = h("input", {
    type: "checkbox",
    "data-role": "prior-payment-move-check",
  });
  const attachMoveConfirm = h(
    "label",
    {
      class: "make-rule-opt-in paperwork-check",
      "data-role": "prior-payment-move-confirm",
      hidden: "true",
    },
    [attachMoveCheck, ` ${ATTACH_MOVE_CONFIRM_COPY}`],
  );
  const attachButton = button("Attach selected", "primary", {
    "data-action": "attach-prior-payments",
    disabled: "true",
  });
  root.append(attachNon1099Confirm, attachMoveConfirm, attachButton);

  function selectedIdentity(): VendorIdentitySnapshotItem | undefined {
    return snapshot?.identities.find((item) => item.vendorId === selectedVendorId);
  }

  function selectedAttachIds(): string[] {
    return [
      ...list.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-plaid-id]',
      ),
    ]
      .filter((box) => box.checked)
      .map((box) => box.dataset.plaidId ?? "")
      .filter((id) => id.length > 0);
  }

  function selectedNeedMove(): boolean {
    const vendorId = selectedVendorId;
    return [
      ...list.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-plaid-id]',
      ),
    ]
      .filter((box) => box.checked)
      .some((box) => {
        const current = box.dataset.attachedVendor ?? "";
        return current.length > 0 && current !== vendorId;
      });
  }

  function paintAttachButton(): void {
    const ids = selectedAttachIds();
    const identity = selectedIdentity();
    const canAttach = identity !== undefined && attachable(identity);
    const needsNon1099Confirm = identity?.classification === "non_1099_vendor";
    const confirmed = !needsNon1099Confirm || attachNon1099Check.checked;
    const needsMove = selectedNeedMove();
    const moveConfirmed = !needsMove || attachMoveCheck.checked;
    attachButton.disabled = ids.length === 0 || !canAttach || !confirmed || !moveConfirmed;
    if (attachButton.disabled) attachButton.setAttribute("disabled", "true");
    else attachButton.removeAttribute("disabled");
    attachNon1099Confirm.hidden = !needsNon1099Confirm;
    if (needsNon1099Confirm) attachNon1099Confirm.removeAttribute("hidden");
    else {
      attachNon1099Confirm.setAttribute("hidden", "true");
      attachNon1099Check.checked = false;
    }
    attachMoveConfirm.hidden = !needsMove;
    if (needsMove) attachMoveConfirm.removeAttribute("hidden");
    else {
      attachMoveConfirm.setAttribute("hidden", "true");
      attachMoveCheck.checked = false;
    }
  }

  function paintVendorPicker(): void {
    clear(vendorHost);
    const identities = snapshot?.identities ?? [];
    if (snapshot === null || !snapshot.present || identities.length === 0) {
      vendorHost.append(
        h("p", { class: "empty empty-state", "data-role": "attached-payments-no-vendors" }, [
          `No vendors are on file yet. Declare a vendor in ${navLabel("queue")}, then come back here after the books update.`,
        ]),
      );
      selectedVendorId = "";
      return;
    }
    const select = h("select", {
      class: "field-input",
      "data-role": "attached-payments-vendor",
      "aria-label": "Minted vendor",
    });
    select.append(h("option", { value: "" }, ["Choose a vendor"]));
    const detailList = h("ul", {});
    for (const item of identities) {
      if (item.vendorId.startsWith("queue:")) continue;
      const option = h(
        "option",
        { value: item.vendorId },
        [`${amVisibleDisplayText(item.matchText)} · ${classificationLabel(item)}`],
      );
      if (!attachable(item)) option.disabled = true;
      if (item.vendorId === selectedVendorId) option.selected = true;
      select.append(option);
      detailList.append(
        h("li", {}, [
          safeText(
            `${amVisibleDisplayText(item.matchText)} · ${amVisibleDisplayText(item.vendorId)}`,
          ),
        ]),
      );
    }
    select.addEventListener("change", () => {
      selectedVendorId = select.value;
      paintCandidates();
    });
    vendorHost.append(
      h("label", { class: "field" }, [
        h("span", { class: "field-label" }, ["Vendor"]),
        h("span", { class: "select-shell" }, [
          select,
          h("span", { class: "select-chevron", "aria-hidden": "true" }, [svgIcon("chevron")]),
        ]),
      ]),
    );
    vendorHost.append(disclosure("Show details", detailList));
  }

  function paintCandidates(): void {
    clear(list);
    const windowCaption = root.querySelector('[data-role="attached-payments-window"]');
    if (windowCaption) {
      windowCaption.textContent = `Window is 1 January through 31 December ${loadedYear}.`;
    }
    if (candidates.length === 0) {
      list.append(
        h("p", { class: "muted empty-state", "data-role": "prior-payment-empty" }, [
          `${attachPriorPaymentsEmptyCopy(loadedYear)} Choose another year, or come back after a payment is posted.`,
        ]),
      );
      paintAttachButton();
      return;
    }
    for (const row of candidates) {
      const twins = row.cohortPlaidIds.length > 1;
      const box = h("input", {
        type: "checkbox",
        "data-plaid-id": row.plaidTransactionId,
        "data-cohort": row.cohortPlaidIds.join(","),
        "data-attached-vendor": row.attachedVendorId ?? "",
        "aria-label": `Attach ${amVisibleDisplayText(row.merchant ?? row.plaidTransactionId)}`,
      });
      box.addEventListener("change", () => {
        const cohort = (box.dataset.cohort ?? "").split(",").filter(Boolean);
        if (cohort.length > 1) {
          for (const twinId of cohort) {
            const twin = list.querySelector<HTMLInputElement>(
              `input[data-plaid-id="${twinId}"]`,
            );
            if (twin) twin.checked = box.checked;
          }
        }
        paintAttachButton();
      });
      const attached = row.attachedVendorId !== null;
      if (attached && row.attachedVendorId === selectedVendorId && selectedVendorId !== "") {
        box.disabled = true;
      }
      const detachBtn = attached
        ? button("Detach", "quiet", {
            class: "btn-ghost",
            "data-action": "detach-prior-payment",
            "data-plaid-id": row.plaidTransactionId,
          })
        : null;
      if (detachBtn) {
        detachBtn.addEventListener("click", () => {
          // One press, one request: the write gate disables the row while it
          // runs (rd round 12, A5-1).
          void (async () => {
            const outcome = await runWrite({
              key: `detach:${row.plaidTransactionId}`,
              scope: detachBtn.parentElement ?? list,
              gate: () => null,
              send: () =>
                client.detachPriorPayment(company, { plaidId: row.plaidTransactionId }),
              // The re-read is part of the landed write: its failure is the
              // list's, never the detach's (R7-13).
              afterLanded: () => loadCandidates(),
              stillCurrent,
            });
            if (!stillCurrent()) return;
            if (outcome.kind !== "landed") {
              paintLiveCopy(writeFailureCopy(outcome, role));
              return;
            }
            if (outcome.afterLandedFailed === true) {
              paintListReloadFailed("Detached.");
              return;
            }
            paintLive("Detached.");
          })();
        });
      }
      const incumbentId = row.attachedVendorId ?? "";
      // Built outside the h() children: the account path is an internal name
      // and only its words go on the face.
      const accountWords = ` · ${accountLabel(row.account, companies)}`;
      list.append(
        h("div", { class: "prior-payment-row", "data-role": "prior-payment-row" }, [
          h("label", { class: "checkbox-row paperwork-check" }, [
            box,
            h("span", { class: "prior-payment-copy" }, [
              h("strong", { "data-role": "prior-payment-merchant" }, [
                amVisibleDisplayText(row.merchant ?? "—"),
              ]),
              ` · ${formatDisplayDate(row.txnDate)} · ${formatAmount(row.amount)}`,
              accountWords,
              row.location ? ` · ${amVisibleDisplayText(row.location)}` : "",
              " · Payee on the books ",
              h("span", { "data-role": "prior-payment-ledger-payee" }, [
                amVisibleDisplayText(row.ledgerPayee),
              ]),
              twins ? ` · ${ATTACH_TWINS_COPY}` : "",
              attached ? " · attached to " : "",
              ...(attached
                ? [
                    h("span", { "data-role": "prior-payment-incumbent" }, [
                      vendorFaceLabel(incumbentId, snapshot?.identities ?? []),
                    ]),
                  ]
                : []),
            ]),
          ]),
          ...(attached
            ? [disclosure("Show details", h("p", {}, [amVisibleDisplayText(incumbentId)]))]
            : []),
          ...(detachBtn ? [detachBtn] : []),
        ]),
      );
    }
    paintAttachButton();
  }

  async function loadCandidates(): Promise<void> {
    // One token per read. A delayed answer for a year the person has already
    // left is dropped, so rows and the window caption always describe the
    // year that was loaded (R7-13).
    const token = ++loadToken;
    const wantedYear = year;
    const page = await client.listPriorPaymentCandidates(
      company,
      candidatesPeriodForYear(wantedYear),
    );
    if (!stillCurrent() || token !== loadToken) return;
    candidates = page.candidates;
    loadedYear = wantedYear;
    paintCandidates();
  }

  async function load(): Promise<void> {
    snapshot = await client.getVendorIdentities(company);
    if (!stillCurrent()) return;
    paintVendorPicker();
    await loadCandidates();
  }

  async function changeYear(delta: number): Promise<void> {
    const wantedYear = String(Number(year) + delta);
    const token = ++loadToken;
    year = wantedYear;
    yearLabel.textContent = year;
    try {
      const page = await client.listPriorPaymentCandidates(
        company,
        candidatesPeriodForYear(wantedYear),
      );
      if (!stillCurrent() || token !== loadToken) return;
      candidates = page.candidates;
      loadedYear = wantedYear;
      paintCandidates();
    } catch (error) {
      if (!stillCurrent() || token !== loadToken) return;
      console.error("prior payments load failed", error);
      // The read failed: go back to the year the rows and caption still show,
      // so the chevrons cannot walk away from what is on screen.
      year = loadedYear;
      yearLabel.textContent = year;
      paintLive("Couldn't load payments for that year. Try again.");
    }
  }

  prev.addEventListener("click", () => {
    void changeYear(-1);
  });
  next.addEventListener("click", () => {
    void changeYear(1);
  });

  function paintListReloadFailed(success: string): void {
    clear(live);
    live.append(
      document.createTextNode(
        success.length > 0
          ? `${success} The list did not reload.`
          : "The list did not reload.",
      ),
    );
    const retry = button("Try again", "secondary", {
      "data-action": "retry-attached-payments-list",
      "aria-label": "Try again",
    });
    retry.addEventListener("click", () => {
      void (async () => {
        try {
          await loadCandidates();
        } catch (error) {
          if (!stillCurrent()) return;
          console.error("prior payments reload failed", error);
          paintListReloadFailed("");
        }
      })();
    });
    live.append(retry);
  }

  attachButton.addEventListener("click", () => {
    // One press, one request: the write gate refuses a second press while the
    // first is in flight (R7-13, mirrors Detach).
    void (async () => {
      const ids = [...new Set(selectedAttachIds())];
      const identity = selectedIdentity();
      if (ids.length === 0 || identity === undefined || !attachable(identity)) {
        return;
      }
      const outcome = await runWrite({
        key: `attach:${identity.vendorId}:${ids.join(",")}`,
        scope: attachButton.parentElement ?? root,
        gate: () => null,
        send: () =>
          client.attachPriorPayments(company, {
            vendorId: identity.vendorId,
            plaidIds: ids,
          }),
        afterLanded: () => loadCandidates(),
        stillCurrent,
      });
      if (!stillCurrent()) return;
      if (outcome.kind !== "landed") {
        paintLiveCopy(writeFailureCopy(outcome, role));
        return;
      }
      if (outcome.afterLandedFailed === true) {
        paintListReloadFailed("Attached.");
      } else {
        paintLive("Attached.");
      }
      // The repainted rows have no selection, so the button is off again after
      // the press guard hands the controls back.
      setTimeout(paintAttachButton, 0);
    })();
  });
  attachNon1099Check.addEventListener("change", () => {
    paintAttachButton();
  });
  attachMoveCheck.addEventListener("change", () => {
    paintAttachButton();
  });

  function paintLoadFailure(host: HTMLElement): void {
    clear(host);
    const retry = button("Try again", "primary", {
      "data-action": "retry-attached-payments",
      "aria-label": "Try again",
    });
    retry.addEventListener("click", () => {
      void (async () => {
        try {
          clear(host);
          host.append(document.createTextNode("Loading…"));
          await load();
        } catch (retryError) {
          if (!stillCurrent()) return;
          console.error("attached payments load failed", retryError);
          paintLoadFailure(host);
        }
      })();
    });
    host.append(
      h("div", { class: "error-state", role: "alert", "data-role": "attached-payments-error" }, [
        h("p", {}, ["Couldn't load attached payments. Try again."]),
        retry,
      ]),
    );
  }

  try {
    await load();
  } catch (error) {
    if (!stillCurrent()) return;
    if (
      error instanceof AdminApiError &&
      (error.status === 401 || error.status === 403 || error.status === 503)
    ) {
      throw error;
    }
    console.error("attached payments load failed", error);
    paintLoadFailure(live);
  }

  function paintLive(message: string): void {
    clear(live);
    const requestId = /request id\s+(\S+)/.exec(message);
    if (requestId !== null) {
      live.append(
        document.createTextNode(
          "Could not attach. Try again, or show details if you need to ask for help.",
        ),
      );
      live.append(disclosure("Show details", h("p", {}, [amVisibleDisplayText(message)])));
      return;
    }
    live.append(document.createTextNode(amVisibleDisplayText(message)));
  }

  /** A failure sentence on the face, the server's words only in Show details. */
  function paintLiveCopy(copy: { sentence: string; detail: string | null }): void {
    clear(live);
    live.append(document.createTextNode(copy.sentence));
    if (copy.detail !== null) {
      live.append(disclosure("Show details", h("p", {}, [copy.detail])));
    }
  }

  /** The one way an attached-payment write's outcome becomes words (§3.1). */
  function writeFailureCopy(
    outcome: WriteOutcome,
    writer: StaffRole,
  ): { sentence: string; detail: string | null } {
    if (outcome.kind !== "landed" && outcome.error instanceof AdminApiError) {
      const copy = failureCopy(outcome.error, { role: writer, context: "write" });
      if (outcome.error.status === 422) {
        // The Worker's two attach refusals have their own decided sentence
        // (group b1 owns both; this page keeps reading them).
        if (copy.detail === ATTACH_TWINS_WORKER_MESSAGE) {
          return { sentence: ATTACH_TWINS_COPY, detail: copy.detail };
        }
        if (copy.detail === ATTACH_NOT_ATTACHABLE_WORKER_MESSAGE) {
          return { sentence: ATTACH_NOT_ATTACHABLE_COPY, detail: copy.detail };
        }
      }
      return copy;
    }
    return {
      sentence: outcome.kind === "landed" ? "" : outcomeSentence(outcome, writer),
      detail: null,
    };
  }
}
